import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Badge, Image } from "react-bootstrap";
import { useParams, Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedinIn,
} from "react-icons/fa";
import { useGetBlogBySlugQuery } from "../../../redux/api/BlogsApi/blogsApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import SEO from "../../../Components/SEO";
import "./BlogDetail.css";

const BlogDetail = () => {
  const { slug } = useParams();
  const { data: blog, isLoading, error } = useGetBlogBySlugQuery(slug);
  const [activeId, setActiveId] = useState("");

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Parse headings for TOC and inject IDs into content
  const { toc, processedContent } = useMemo(() => {
    if (!blog?.content) return { toc: [], processedContent: "" };

    const parser = new DOMParser();
    const doc = parser.parseFromString(blog.content, "text/html");
    const headings = doc.querySelectorAll("h1, h2, h3, h4");
    const extractedToc = [];

    headings.forEach((heading, index) => {
      const text = heading.innerText;
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-" + index;
      heading.setAttribute("id", id);

      extractedToc.push({
        id,
        label: text,
        level: parseInt(heading.tagName.substring(1)),
      });
    });

    return {
      toc: extractedToc,
      processedContent: doc.body.innerHTML,
    };
  }, [blog?.content]);

  // Active heading observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "0% 0% -80% 0%" },
    );

    const headings = document.querySelectorAll(
      ".blog-main-content h1, .blog-main-content h2, .blog-main-content h3",
    );
    headings.forEach((h) => observer.observe(h));

    return () => observer.disconnect();
  }, [processedContent]);

  if (isLoading)
    return (
      <div className="py-5 text-center">
        <LoaderSpinner />
      </div>
    );
  if (error || !blog)
    return (
      <div className="py-5 text-center">
        <h3>Blog not found</h3>
        <Link to="/blogs">Back to Blogs</Link>
      </div>
    );

  return (
    <div className="blog-detail-container pb-5">
      <SEO
        title={
          blog.meta_title || `${blog.title} | Ideal Corporate Services Blog`
        }
        description={blog.meta_description}
        keywords={blog.meta_keyword}
      />
      <Container>
        {/* Header Section */}
        <section className="blog-detail-header text-center">
          <Badge className="blog-category-badge mb-3">
            {blog.category?.name || "Insights"}
          </Badge>
          <h1 className="blog-detail-title">{blog.title}</h1>
          {blog.summary && (
            <p className="blog-detail-subtitle">{blog.summary}</p>
          )}
          <div className="reading-time mt-3">
            Published on:{" "}
            <span>
              {new Date(blog.published_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
        </section>

        <div className="blog-meta-divider" />

        {/* Featured Image */}
        <div className="text-center mb-5">
          <Image
            src={blog.image || "https://via.placeholder.com/1200x600"}
            className="img-fluid"
            style={{ maxHeight: "400px", width: "auto", objectFit: "contain" }}
          />
        </div>

        {/* Author and Share Section */}
        <Row className="justify-content-center align-items-center mb-5 gx-5">
          <Col xs="auto">
            <div className="author-section">
              <span className="author-label text-muted">Author:</span>
              <div className="author-info d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-bold">{blog.author || "Ideal Corp"}</h6>
              </div>
            </div>
          </Col>
          <Col xs="auto">
            <div className="share-section">
              <span className="share-label text-muted">Share:</span>
              <div className="social-icons">
                <FaFacebookF className="social-icon" />
                <FaInstagram className="social-icon" />
                <FaTwitter className="social-icon" />
                <FaLinkedinIn className="social-icon" />
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          {/* Sidebar TOC - Sticky */}
          <Col lg={4} className="mb-4 d-none d-lg-block">
            <div className="sidebar-toc shadow-sm" style={{ top: "100px" }}>
              <h4 className="toc-title mb-4">In This Article</h4>
              <ul className="toc-list list-unstyled ps-0">
                {toc.map((item) => (
                  <li
                    key={item.id}
                    className={`toc-item mb-2 ${item.level > 2 ? "ms-3" : ""}`}
                  >
                    <a
                      href={`#${item.id}`}
                      className={`toc-link text-decoration-none ${activeId === item.id ? "active fw-bold text-primary" : "text-muted"}`}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
                {toc.length === 0 && (
                  <li className="text-muted small">No sections found.</li>
                )}
              </ul>
            </div>
          </Col>

          {/* Main Content */}
          <Col lg={8}>
            <div
              className="blog-main-content"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlogDetail;
